<template>
  <div
    v-show="resultsCount > 0"
    class="search-modal__wrapper"
  >
    <search-form
      v-if="view !== 'desktop-form'"
      ref="searchForm"
      view="mobile-form"
      @update:query="computedQuery = $event"
    />

    <div class="search-modal grid container">
      <algolia-stats-connector @nb-hits-change="resultsChange" />
      <div
        v-show="computedQuery"
        class="col xs4 l1-3 search-modal__group-wrapper"
      >
        <ais-index :index-name="algolia.indexSuggestions">
          <ais-configure :hits-per-page.camel="3" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.suggestions_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'search')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="query"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>
              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <hr
          v-show="view === 'desktop-form'"
          class="search-modal__divider"
        >

        <ais-index
          v-show="view === 'desktop-form'"
          :index-name="algolia.indexCollections"
        >
          <algolia-stats-connector
            @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexCollections)"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.collections_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'collections')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <hr
          v-show="view === 'desktop-form'"
          class="search-modal__divider"
        >

        <ais-index
          v-show="view === 'desktop-form'"
          :index-name="algolia.indexPages"
        >
          <algolia-stats-connector
            @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexPages)"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.pages_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'pages')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <a
          v-if="!isMobile && overlay.isActive"
          class="search-modal__logo"
          :href="$variable('routes.root')"
          :aria-label="$string('general.site_header.return_home')"
        >
          <icon-logo />
        </a>
      </div>

      <div
        v-show="!computedQuery"
        class="col xs4 l1-3 search-modal__group-wrapper"
      >
        <ais-index :index-name="algolia.indexSuggestions">
          <ais-configure :hits-per-page.camel="6" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.popular_searches_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'search')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="query"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>
              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <a
          v-if="overlay.isActive && !isMobile"
          class="search-modal__logo"
          :href="$variable('routes.root')"
          :aria-label="$string('general.site_header.return_home')"
        >
          <icon-logo />
        </a>
      </div>

      <hr class="search-modal__divider--vertical">

      <div class="col xs4 l5-12 search-modal__products-wrapper">
        <ais-index :index-name="computedQuery ? algolia.indexProducts : algolia.indexTrendingProducts">
          <algolia-stats-connector
            @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexProducts)"
          />
          <ais-configure
            distinct
            :hits-per-page.camel="view === 'desktop-form' ? 3 : 4"
            :faceting-after-distinct.camel="true"
            :filters="algolia.defaultFilters"
          />

          <ais-hits
            :class-names="{
              'ais-Hits': `search-modal__group ${productsCount > 0 ? 'small-gap' : ''}`,
            }"
            :transform-items="transformItems"
          >
            <template #default="{ items }">
              <div class="search-modal__group-header">
                <p
                  v-if="computedQuery"
                  class="search-modal__group-label text-body-xs"
                  v-text="$string('predictive_search.products_label')"
                />
                <p
                  v-else
                  class="search-modal__group-label text-body-xs"
                  v-text="$string('predictive_search.featured_products_label')"
                />

                <a
                  v-if="computedQuery && productsCount > 0"
                  class="search-modal__group-cta text-body-3xs-link"
                  :href="getUrl({ query: computedQuery }, 'search')"
                  v-text="$string('predictive_search.view_results', {
                    replace: {
                      count: productsCount,
                    },
                  })"
                />
              </div>

              <ul
                v-if="items.length"
                class="search-modal__products"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                  class="search-modal__product"
                >
                  <product-card
                    v-if="overlay.isActive"
                    :badge="generateProductBadge(item)"
                    :product="item"
                    :position="item['__position']"
                    section-type="search modal"
                    section-name="search modal"
                  />
                </li>
              </ul>
              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>
      </div>

      <template v-if="view !== 'desktop-form' && computedQuery">
        <hr class="search-modal__divider">

        <ais-index
          :index-name="algolia.indexCollections"
          class="col xs4"
        >
          <algolia-stats-connector
            @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexCollections)"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.collections_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'collections')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <hr class="search-modal__divider">

        <ais-index
          :index-name="algolia.indexPages"
          class="col xs4"
        >
          <algolia-stats-connector
            @nb-hits-change="(nbHits) => nbHitsChange(nbHits, algolia.indexPages)"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.pages_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'pages')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>
      </template>
    </div>
  </div>

  <empty-search-state v-if="resultsCount === 0" />
</template>

<script>

/**
 * Vue: Search overlay (search-modal)
 * -----------------------------------------------------------------------------
 * Predictive search overlay.
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapState } from 'vuex'
import { AisConfigure, AisHits, AisIndex, AisHighlight } from 'vue-instantsearch/vue3/es'
import ProductCard from '~async/product-card/product-card'
import AlgoliaStatsConnector from '~async/algolia/algolia-stats-connector'
import EmptySearchState from '~async/empty-search-state/empty-search-state'
import SearchForm from '~async/search-form/search-form'

import { formatAlgoliaProduct, generateProductBadge } from '~/helpers/format-algolia'
import IconLogo from '~icons/misc/logo.svg'

export default {
  name: 'SearchModal',

  components: {
    AisIndex,
    AisHits,
    AisConfigure,
    AisHighlight,
    AlgoliaStatsConnector,
    ProductCard,
    EmptySearchState,
    SearchForm,
    IconLogo,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    query: {
      type: String,
      default: '',
    },
    focusElements: {
      type: [null, Array],
      default: () => [],
    },
    view: {
      // 'desktop-form', 'mobile-form' or 'toggle-modal'
      type: String,
      default: 'desktop-form',
    },
  },

  emits: ['update:query'],

  data() {
    return {
      productsCount: -1,
      collectionsCount: 0,
      pagesCount: 0,
      computedQuery: this.query,
      algolia: {
        indexProducts: this.$variable('algolia.indexes.products'),
        indexTrendingProducts: this.$variable('algolia.indexes.trendingProducts'),
        indexCollections: this.$variable('algolia.indexes.collections'),
        indexPages: this.$variable('algolia.indexes.pages'),
        indexArticles: this.$variable('algolia.indexes.articles'),
        indexSuggestions: this.$variable('algolia.indexes.suggestions'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Returns the total number of results.
     * @returns {Number} - Total search results.
     */
    resultsCount() {
      return this.productsCount + this.collectionsCount + this.pagesCount
    },
  },

  watch: {
    async 'overlay.isActive'() {
      if (this.overlay.isActive && this.focusElements) {
        await this.$nextTick()

        this.overlay.update({
          focusElements: {
            prepend: true,
            elements: this.focusElements,
          },
        })
      }
    },

  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {

    /**
     * Close overlay on escape key press.
     * @param {Event} event - Keydown event.
     */
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.overlay.close()
      }
    },

    /**
     * Transform items to properly pass props to the product card component.
     * @param {Array} items - Algolia items.
     * @returns {Array}
     */
    transformItems(items) {
      const data = items.map((item) => formatAlgoliaProduct(item))

      return data
    },

    /**
     * Set number of hits per algolia index.
     * @param {Number} nbHits - number of hits/results.
     * @param {String} algoliaIndex - Algolia index name.
     */
    nbHitsChange(nbHits, algoliaIndex) {
      switch (algoliaIndex) {
        case this.algolia.indexProducts:
          this.productsCount = nbHits
          break
        case this.algolia.indexCollections:
          this.collectionsCount = nbHits
          break
        case this.algolia.indexPages:
          this.pagesCount = nbHits
          break
      }
    },

    /**
     * Update focus trap on results change.
     */
    async resultsChange() {
      if (!this.overlay.isActive) {
        return
      }

      await this.$nextTick()

      this.overlay.update({
        focusElements: {
          prepend: true,
          elements: this.focusElements,
        },
      })
    },

    /**
     * Generates a badge object from the given product.
     * @param {Object} - Product object.
     * @returns {Object}
     */
    generateProductBadge(product) {
      return generateProductBadge(product)
    },

    /**
     * Get url for search page including Root route.
     */
    getUrl(record, type) {
      switch (type) {
        case 'search':
          return `${this.$variable('routes.search')}?query=${record.query}`
        case 'collections':
          return `${this.$variable('routes.root')}collections/${record.handle}`
        case 'pages':
          return `${this.$variable('routes.root')}pages/${record.handle}`
        case 'popular':
          return `${this.$variable('routes.root')}search?query=${record}`
        default:
          return ''
      }
    },
  },
}
</script>

<style lang="scss">
@import './search-modal';
</style>
